import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { useConfirm } from "../context/ConfirmContext";
import { useToast } from "../context/ToastContext";
import { LoadingOverlay } from "../components/LoadingOverlay";
import {
  getPreferredCompanies, savePreferredCompanies, resetPreferredCompanies,
  getScoringDefaults, saveScoringDefaults, ScoringDefaults,
  getScreeningDefaults, saveScreeningDefaults, ScreeningDefaults,
  getApplicantNotifyPref, saveApplicantNotifyPref,
  getQuestionTemplates, saveQuestionTemplates, QuestionTemplate, QuestionFieldType,
  getSettingsBranding, saveBranding, getErrorMessage,
} from "../api";
import { FIELD_TYPE_LABELS, needsOptions } from "../components/JobScreeningQuestions";
import { useAuth } from "../context/AuthContext";

const NAVY = "#1e3a8a";
const card: React.CSSProperties = { background: "white", border: "1px solid #e2e8f0", borderRadius: 10, padding: 24, marginBottom: 16, boxShadow: "0 1px 3px rgba(0,0,0,0.05)" };
const saveBtn: React.CSSProperties = { background: NAVY, color: "white", border: "none", padding: "9px 18px", borderRadius: 7, cursor: "pointer", fontWeight: 600, fontSize: 13 };
const outlineBtn: React.CSSProperties = { background: "none", border: "1px solid #e2e8f0", borderRadius: 7, padding: "8px 14px", fontSize: 12, color: "#64748b", cursor: "pointer" };
const inputStyle: React.CSSProperties = { border: "1px solid #d1d5db", borderRadius: 7, padding: "8px 10px", fontSize: 13, outline: "none" };
const labelStyle: React.CSSProperties = { display: "block", fontSize: 13, fontWeight: 600, color: "#334155", marginBottom: 6 };
const reorderBtnStyle: React.CSSProperties = { background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: 10, padding: 0, lineHeight: 1 };
// Matches Settings' card heading so the moved intake cards read as native here.
const sectionHeadStyle: React.CSSProperties = { color: NAVY, fontSize: 15, margin: "0 0 16px", borderBottom: "2px solid #00c9a7", paddingBottom: 6 };

const TABS = [
  { key: "companies", label: "Companies" },
  { key: "intake", label: "Intake", settingsAdminOnly: true },
  { key: "scoring", label: "Scoring" },
  { key: "screening", label: "Screening" },
  { key: "templates", label: "Question Templates" },
  { key: "notifications", label: "Notifications" },
];

const ROLE_TYPES = ["", "Technical", "Management", "Sales/BD", "Creative", "Operations"];
const KNOCKOUT_TYPES = [
  { value: "min_experience", label: "Min experience", placeholder: "e.g. 5 years" },
  { value: "required_degree", label: "Required degree", placeholder: "e.g. Bachelor" },
  { value: "location", label: "Must be in location", placeholder: "e.g. Bangkok" },
];
const RED_FLAG_LABELS: Record<string, string> = {
  job_hopping: "Job hopping (avg tenure < 12 months)",
  long_gap: "Long employment gap (> 6 months)",
  title_regression: "Title regression (senior → junior)",
  frequent_role_changes: "Frequent role changes (4+ employers in 5 years)",
};

export default function RecruiterPreferencesPage() {
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();

  // Intake settings read and write through /api/settings/branding, whose dependency is
  // `require_admin_or_assistant` — admin, admin_assistant *and* hr (deps.py). Mirror that
  // dependency exactly rather than approximating it with an admin check. Dropping hr did
  // not keep an existing restriction: these fields lived in Settings → Branding, a tab hr
  // could always open, so the move here silently revoked access hr already had. It also
  // stranded them, because the Recruitment setup card is served under the same backend
  // dependency — hr is shown a "Preferred candidate location" item linking to ?tab=intake,
  // and on arrival the tab is filtered out and the page falls back to Companies with no
  // explanation. Widen or narrow this only alongside the endpoint it stands in for.
  const canEditIntake = user?.role === "admin" || user?.role === "admin_assistant"
    || user?.role === "hr" || !!user?.is_super_admin;
  const tabs = TABS.filter(t => !t.settingsAdminOnly || canEditIntake);

  const requested = params.get("tab");
  const tab = tabs.some(t => t.key === requested) ? (requested as string) : "companies";

  return (
    <div style={{ maxWidth: 860 }}>
      {/* No page-header here — the app shell already renders the h1 and subtitle for
          this route (App.tsx PAGE_TITLES / PAGE_SUBTITLES). Adding one duplicates it. */}
      <div style={{ display: "flex", gap: 4, marginBottom: 16, borderBottom: "1px solid #e2e8f0", overflowX: "auto" }}>
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setParams(t.key === "companies" ? {} : { tab: t.key })}
            style={{
              background: "none", border: "none", cursor: "pointer", whiteSpace: "nowrap",
              padding: "10px 16px", fontSize: 13, fontWeight: 600,
              color: tab === t.key ? NAVY : "#64748b",
              borderBottom: tab === t.key ? `2px solid ${NAVY}` : "2px solid transparent",
              marginBottom: -1,
            }}
          >{t.label}</button>
        ))}
      </div>

      {tab === "companies" && <CompaniesTab />}
      {tab === "intake" && canEditIntake && <IntakeTab />}
      {tab === "scoring" && <ScoringTab />}
      {tab === "screening" && <ScreeningTab />}
      {tab === "templates" && <TemplatesTab />}
      {tab === "notifications" && <NotificationsTab />}
    </div>
  );
}

// ── Intake (moved out of Settings → Branding) ───────────────────────────────
//
// Jobs email, preferred candidate location and the CV intake limits are
// recruiting configuration, but they used to sit in Core's Branding tab — where
// a workspace without Recruitment was shown them, and where the jobs email was
// a *required* field that blocked saving the company profile entirely. They keep
// their existing setting keys and endpoint; only their home moves.
// Spec: .claude/specs/module-aware-core-setup.md

function IntakeTab() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { data: branding, isLoading } = useQuery({ queryKey: ["settings-branding"], queryFn: getSettingsBranding });
  const [form, setForm] = useState({
    company_jobs_email: "", company_location: "",
    max_file_mb: "5", max_per_sender_day: "3", max_daily: "50",
  });
  const [saved, setSaved] = useState(false);

  React.useEffect(() => {
    if (branding) setForm({
      company_jobs_email: branding.company_jobs_email || "",
      company_location:   branding.company_location || "",
      max_file_mb:        branding.cv_max_file_mb || "5",
      max_per_sender_day: branding.cv_max_per_sender_day || "3",
      max_daily:          branding.cv_max_daily || "50",
    });
  }, [branding]);

  const mutation = useMutation({
    mutationFn: () => saveBranding({
      company_jobs_email:      form.company_jobs_email,
      company_location:        form.company_location,
      "cv.max_file_mb":        form.max_file_mb,
      "cv.max_per_sender_day": form.max_per_sender_day,
      "cv.max_daily":          form.max_daily,
    }),
    onSuccess: () => {
      setSaved(true);
      qc.invalidateQueries({ queryKey: ["settings-branding"] });
      setTimeout(() => setSaved(false), 3000);
    },
    onError: (err) => toast(getErrorMessage(err, "Failed to save intake settings"), "error"),
  });

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }));

  // Still required — the backend enforces it for tenants that own Recruitment,
  // which is every tenant that can see this tab.
  const missingRequired = !form.company_jobs_email.trim();

  return (
    <>
      <LoadingOverlay active={isLoading || mutation.isPending} />
      <div style={card}>
        <h3 style={sectionHeadStyle}>CV Intake</h3>
        <p style={{ fontSize: 13, color: "#64748b", marginBottom: 20 }}>
          Where CVs arrive and which location the AI scorer should favour.
        </p>
        <div style={{ display: "grid", gap: 14 }}>
          <div>
            <label style={labelStyle}>Recruitment / Jobs Email *</label>
            <input value={form.company_jobs_email} onChange={set("company_jobs_email")}
              placeholder="jobs@yourdomain.com" style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }} />
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 3 }}>
              CVs emailed here are ingested automatically. Also printed in the footer of formatted CVs sent to clients.
            </div>
          </div>
          <div>
            <label style={labelStyle}>Preferred Candidate Location</label>
            <input value={form.company_location} onChange={set("company_location")}
              placeholder="Bangkok, Thailand" style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }} />
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 3 }}>Hints the AI scorer to favour candidates in this location.</div>
          </div>
        </div>
      </div>

      <div style={card}>
        <h3 style={sectionHeadStyle}>CV Intake Limits</h3>
        <p style={{ fontSize: 13, color: "#64748b", marginBottom: 16 }}>
          Protect against spam and control AI API costs. CVs that exceed these limits are silently dropped.
        </p>
        <div className="grid-3col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
          <div>
            <label style={labelStyle}>Max File Size (MB)</label>
            <input type="number" min="1" max="20" value={form.max_file_mb} onChange={set("max_file_mb")}
              style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }} />
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 3 }}>CVs larger than this are ignored.</div>
          </div>
          <div>
            <label style={labelStyle}>Max CVs per Sender / Day</label>
            <input type="number" min="1" max="20" value={form.max_per_sender_day} onChange={set("max_per_sender_day")}
              style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }} />
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 3 }}>Same email can't flood the queue.</div>
          </div>
          <div>
            <label style={labelStyle}>Max CVs per Day (total)</label>
            <input type="number" min="1" max="500" value={form.max_daily} onChange={set("max_daily")}
              style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }} />
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 3 }}>Hard cap on daily AI usage.</div>
          </div>
        </div>
        <div style={{ marginTop: 20, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <button onClick={() => mutation.mutate()} disabled={mutation.isPending || missingRequired}
            style={{ ...saveBtn, opacity: missingRequired ? 0.5 : 1, cursor: missingRequired ? "not-allowed" : "pointer" }}>
            {mutation.isPending ? "Saving…" : "Save Intake Settings"}
          </button>
          {saved && <span style={{ fontSize: 13, color: "#16a34a" }}>Saved!</span>}
          {missingRequired && <span style={{ fontSize: 12, color: "#dc2626" }}>Recruitment / Jobs Email is required.</span>}
        </div>
      </div>
    </>
  );
}

// ── Companies (preferred-companies watch list — unchanged behavior) ─────────

function CompaniesTab() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const confirm = useConfirm();
  const { data, isLoading } = useQuery({ queryKey: ["preferred-companies"], queryFn: getPreferredCompanies });
  const [companies, setCompanies] = useState<string[]>([]);
  const [input, setInput] = useState("");
  const [saved, setSaved] = useState(false);

  React.useEffect(() => {
    if (data?.companies) setCompanies(data.companies);
  }, [data]);

  const onSaveSuccess = (result: { companies: string[] }) => {
    setCompanies(result.companies);
    setSaved(true);
    qc.invalidateQueries({ queryKey: ["preferred-companies"] });
    setTimeout(() => setSaved(false), 3000);
  };

  const mutation = useMutation({
    mutationFn: (list: string[]) => savePreferredCompanies(list),
    onSuccess: onSaveSuccess,
    onError: () => toast("Failed to save companies", "error"),
  });

  const resetMutation = useMutation({
    mutationFn: resetPreferredCompanies,
    onSuccess: onSaveSuccess,
    onError: () => toast("Failed to reset companies", "error"),
  });

  function addCompany(name: string) {
    const trimmed = name.trim();
    if (!trimmed || companies.includes(trimmed)) return;
    setCompanies(prev => [...prev, trimmed].sort((a, b) => a.localeCompare(b)));
    setInput("");
  }

  function removeCompany(name: string) {
    setCompanies(prev => prev.filter(c => c !== name));
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addCompany(input);
    }
  }

  async function handleReset() {
    const ok = await confirm(
      "This will reset to the default list of ~200 global + Indian + Thai companies. Continue?",
      { confirmLabel: "Reset to Defaults" }
    );
    if (ok) resetMutation.mutate();
  }

  return (
    <>
      <LoadingOverlay active={mutation.isPending || resetMutation.isPending} />
      <div style={card}>
        <p style={{ fontSize: 13, color: "#64748b", marginBottom: 16, lineHeight: 1.6 }}>
          When a candidate's work history includes a company from this list, their profile will show a{" "}
          <strong>🎯 Preferred Company</strong> badge — making it easy to spot talent from target organisations.
          Pre-populated with global top companies + Indian and Thai companies. You can add or remove any company.
        </p>

        {/* Tag input */}
        <div style={{ border: "1px solid #d1d5db", borderRadius: 8, padding: "8px 10px", minHeight: 44, display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", marginBottom: 12 }}>
          {companies.map(c => (
            <span key={c} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 500, color: "#334155" }}>
              {c}
              <button
                onClick={() => removeCompany(c)}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: 14, lineHeight: 1, padding: 0, marginLeft: 2 }}
                title="Remove"
              >×</button>
            </span>
          ))}
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={() => input.trim() && addCompany(input)}
            placeholder={companies.length === 0 ? "Type a company name and press Enter…" : "Add another…"}
            style={{ border: "none", outline: "none", fontSize: 13, minWidth: 180, flex: 1 }}
          />
        </div>

        <p style={{ fontSize: 11, color: "#94a3b8", marginBottom: 16 }}>
          Press Enter or comma to add. Click × to remove. {companies.length} companies in list.
        </p>

        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <button
            onClick={() => mutation.mutate(companies)}
            disabled={mutation.isPending || resetMutation.isPending || isLoading}
            style={saveBtn}
          >
            {mutation.isPending ? "Saving…" : "Save Changes"}
          </button>
          {saved && <span style={{ fontSize: 13, color: "#16a34a" }}>Saved!</span>}
          <button
            onClick={handleReset}
            disabled={mutation.isPending || resetMutation.isPending}
            style={outlineBtn}
          >
            Reset to Defaults
          </button>
        </div>
      </div>

      <div style={{ ...card, background: "#f8fafc" }}>
        <h4 style={{ fontSize: 13, fontWeight: 700, color: "#334155", marginBottom: 10, marginTop: 0 }}>How talent signals work</h4>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 12, color: "#64748b", lineHeight: 1.6 }}>
          <div style={{ display: "flex", gap: 8 }}><span>🎯</span><span><strong>Preferred Company</strong> — candidate has worked at a company from your watch list above.</span></div>
          <div style={{ display: "flex", gap: 8 }}><span>🏢</span><span><strong>Notable Employer</strong> — AI identified a globally or regionally recognised employer in the candidate's history (e.g. FAANG, top bank, top consulting firm).</span></div>
          <div style={{ display: "flex", gap: 8 }}><span>🎓</span><span><strong>Notable Institution</strong> — AI identified a well-regarded university in the candidate's education.</span></div>
          <div style={{ display: "flex", gap: 8 }}><span>⚡</span><span><strong>Fast Progression</strong> — candidate was promoted or took on significantly larger scope faster than typical career pace.</span></div>
          <div style={{ display: "flex", gap: 8 }}><span>✅</span><span><strong>Concrete Achievements</strong> — work history contains specific metrics and outcomes, not just generic duties.</span></div>
          <div style={{ display: "flex", gap: 8 }}><span>📚</span><span><strong>Open Source / Publications</strong> — GitHub projects, papers, patents, or conference talks found.</span></div>
          <p style={{ margin: "8px 0 0", fontSize: 11, fontStyle: "italic" }}>
            Talent signals are informational only and do not affect the AI match score. They help recruiters spot high-potential candidates quickly.
          </p>
        </div>
      </div>
    </>
  );
}

// ── Scoring defaults (pre-fill new jobs; per-job override unchanged) ────────

function ScoringTab() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const confirm = useConfirm();
  const { data, isLoading } = useQuery({ queryKey: ["scoring-defaults"], queryFn: getScoringDefaults });
  const [form, setForm] = useState<ScoringDefaults | null>(null);
  const [saved, setSaved] = useState(false);

  React.useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const onSuccess = (result: ScoringDefaults) => {
    setForm(result);
    setSaved(true);
    qc.invalidateQueries({ queryKey: ["scoring-defaults"] });
    setTimeout(() => setSaved(false), 3000);
  };
  const mutation = useMutation({
    mutationFn: saveScoringDefaults,
    onSuccess,
    onError: () => toast("Failed to save scoring defaults", "error"),
  });

  async function handleReset() {
    const ok = await confirm(
      "Reset scoring defaults? New jobs will start with no preset, no knockout criteria, and all red-flag deductions enabled. Existing jobs are not affected.",
      { confirmLabel: "Reset to Defaults" }
    );
    if (ok) mutation.mutate({ reset: true });
  }

  if (isLoading || !form) return <div style={{ color: "#64748b", fontSize: 13 }}>Loading…</div>;

  return (
    <>
      <LoadingOverlay active={mutation.isPending} />
      <div style={card}>
        <p style={{ fontSize: 13, color: "#64748b", marginBottom: 16, lineHeight: 1.6 }}>
          These defaults <strong>pre-fill new jobs</strong> — you can still override everything on each job.
          Changing them never alters existing jobs or re-scores existing candidates.
        </p>

        <label style={labelStyle}>Default role-type scoring preset</label>
        <select
          value={form.role_type}
          onChange={e => setForm({ ...form, role_type: e.target.value })}
          style={{ ...inputStyle, width: 240, marginBottom: 18 }}
        >
          {ROLE_TYPES.map(rt => <option key={rt} value={rt}>{rt || "None (seniority-based fallback)"}</option>)}
        </select>

        <label style={labelStyle}>Default knockout criteria <span style={{ color: "#94a3b8", fontWeight: 400 }}>(auto-reject if not met — no AI cost)</span></label>
        {form.knockouts.map((k, i) => {
          const kt = KNOCKOUT_TYPES.find(t => t.value === k.type) || KNOCKOUT_TYPES[0];
          return (
            <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center", flexWrap: "wrap" }}>
              <select
                value={k.type}
                onChange={e => setForm({ ...form, knockouts: form.knockouts.map((x, j) => j === i ? { ...x, type: e.target.value } : x) })}
                style={{ ...inputStyle, width: 180 }}
              >
                {KNOCKOUT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <input
                value={k.value}
                placeholder={kt.placeholder}
                onChange={e => setForm({ ...form, knockouts: form.knockouts.map((x, j) => j === i ? { ...x, value: e.target.value } : x) })}
                style={{ ...inputStyle, flex: 1, minWidth: 160 }}
              />
              <button
                onClick={() => setForm({ ...form, knockouts: form.knockouts.filter((_, j) => j !== i) })}
                style={{ ...outlineBtn, padding: "8px 10px" }}
                title="Remove"
              >×</button>
            </div>
          );
        })}
        <button
          onClick={() => setForm({ ...form, knockouts: [...form.knockouts, { type: "min_experience", value: "" }] })}
          style={{ ...outlineBtn, marginBottom: 18 }}
        >+ Add Knockout</button>

        <label style={labelStyle}>Red-flag score deductions</label>
        <p style={{ fontSize: 12, color: "#94a3b8", margin: "0 0 10px" }}>
          Disabled flags are still detected and shown on the candidate — they just stop deducting points.
        </p>
        {Object.entries(RED_FLAG_LABELS).map(([key, label]) => (
          <label key={key} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#334155", marginBottom: 8, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={form.red_flags[key] !== false}
              onChange={e => setForm({ ...form, red_flags: { ...form.red_flags, [key]: e.target.checked } })}
            />
            {label}
          </label>
        ))}

        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 16 }}>
          <button onClick={() => mutation.mutate(form)} disabled={mutation.isPending} style={saveBtn}>
            {mutation.isPending ? "Saving…" : "Save Changes"}
          </button>
          {saved && <span style={{ fontSize: 13, color: "#16a34a" }}>Saved!</span>}
          <button onClick={handleReset} disabled={mutation.isPending} style={outlineBtn}>Reset to Defaults</button>
        </div>
      </div>
    </>
  );
}

// ── Screening defaults (question bank + video-screening defaults) ───────────

function ScreeningTab() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const confirm = useConfirm();
  const { data, isLoading } = useQuery({ queryKey: ["screening-defaults"], queryFn: getScreeningDefaults });
  const [form, setForm] = useState<ScreeningDefaults | null>(null);
  const [saved, setSaved] = useState(false);

  React.useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const onSuccess = (result: ScreeningDefaults) => {
    setForm(result);
    setSaved(true);
    qc.invalidateQueries({ queryKey: ["screening-defaults"] });
    setTimeout(() => setSaved(false), 3000);
  };
  const mutation = useMutation({
    mutationFn: saveScreeningDefaults,
    onSuccess,
    onError: () => toast("Failed to save screening defaults", "error"),
  });

  async function handleReset() {
    const ok = await confirm(
      "Reset screening defaults? Question count returns to 5, auto-invite threshold to 75, and the question bank is cleared.",
      { confirmLabel: "Reset to Defaults" }
    );
    if (ok) mutation.mutate({ reset: true });
  }

  if (isLoading || !form) return <div style={{ color: "#64748b", fontSize: 13 }}>Loading…</div>;

  return (
    <>
      <LoadingOverlay active={mutation.isPending} />
      <div style={card}>
        <p style={{ fontSize: 13, color: "#64748b", marginBottom: 16, lineHeight: 1.6 }}>
          Defaults for AI video screening. The question count steers AI question generation for new
          screening templates; the threshold marks high-scoring candidates as <strong>screening suggested</strong> —
          it never auto-sends invites.
        </p>

        <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginBottom: 18 }}>
          <div>
            <label style={labelStyle}>Questions per screening</label>
            <input
              type="number" min={1} max={10}
              value={form.question_count}
              onChange={e => setForm({ ...form, question_count: Number(e.target.value) })}
              style={{ ...inputStyle, width: 100 }}
            />
          </div>
          <div>
            <label style={labelStyle}>Suggest-screening score threshold</label>
            <input
              type="number" min={0} max={100}
              value={form.auto_invite_threshold}
              onChange={e => setForm({ ...form, auto_invite_threshold: Number(e.target.value) })}
              style={{ ...inputStyle, width: 100 }}
            />
            <span style={{ fontSize: 12, color: "#94a3b8", marginLeft: 8 }}>/100 match score</span>
          </div>
        </div>

        <label style={labelStyle}>Question bank</label>
        <p style={{ fontSize: 12, color: "#94a3b8", margin: "0 0 10px" }}>
          Reusable screening questions, grouped by role family. Insert them into any screening template from the AI Screening page.
        </p>
        {form.question_bank.map((q, i) => (
          <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "flex-start", flexWrap: "wrap" }}>
            <input
              value={q.role_family}
              placeholder="Role family (e.g. Engineering)"
              onChange={e => setForm({ ...form, question_bank: form.question_bank.map((x, j) => j === i ? { ...x, role_family: e.target.value } : x) })}
              style={{ ...inputStyle, width: 200 }}
            />
            <textarea
              value={q.question}
              placeholder="Question text…"
              onChange={e => setForm({ ...form, question_bank: form.question_bank.map((x, j) => j === i ? { ...x, question: e.target.value } : x) })}
              style={{ ...inputStyle, flex: 1, minWidth: 220, minHeight: 40, resize: "vertical", fontFamily: "inherit" }}
            />
            <button
              onClick={() => setForm({ ...form, question_bank: form.question_bank.filter((_, j) => j !== i) })}
              style={{ ...outlineBtn, padding: "8px 10px" }}
              title="Remove"
            >×</button>
          </div>
        ))}
        <button
          onClick={() => setForm({ ...form, question_bank: [...form.question_bank, { role_family: "", question: "" }] })}
          style={{ ...outlineBtn, marginBottom: 18 }}
        >+ Add Question</button>

        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <button onClick={() => mutation.mutate(form)} disabled={mutation.isPending} style={saveBtn}>
            {mutation.isPending ? "Saving…" : "Save Changes"}
          </button>
          {saved && <span style={{ fontSize: 13, color: "#16a34a" }}>Saved!</span>}
          <button onClick={handleReset} disabled={mutation.isPending} style={outlineBtn}>Reset to Defaults</button>
        </div>
      </div>
    </>
  );
}

// ── Question Templates (reusable Application Screening Question templates) ──
// Distinct from ScreeningTab above, which is the unrelated AI Video Screening question bank.

function genTemplateId() {
  return (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `new-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
const BLANK_TEMPLATE = (): QuestionTemplate => ({ id: genTemplateId(), label: "", field_type: "text", options: [], required: false });

function TemplatesTab() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const confirm = useConfirm();
  const { data, isLoading } = useQuery({ queryKey: ["question-templates"], queryFn: getQuestionTemplates });
  const [form, setForm] = useState<QuestionTemplate[] | null>(null);
  const [saved, setSaved] = useState(false);

  React.useEffect(() => {
    if (data) setForm(data.templates);
  }, [data]);

  const mutation = useMutation({
    mutationFn: saveQuestionTemplates,
    onSuccess: (result) => {
      setForm(result.templates);
      setSaved(true);
      qc.invalidateQueries({ queryKey: ["question-templates"] });
      setTimeout(() => setSaved(false), 3000);
    },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to save question templates", "error"),
  });

  async function handleReset() {
    const ok = await confirm(
      "Reset question templates to the 5 starter templates? Any custom templates you've added or edited will be lost.",
      { confirmLabel: "Reset to Defaults" }
    );
    if (ok) mutation.mutate({ reset: true });
  }

  function updateRow(i: number, patch: Partial<QuestionTemplate>) {
    setForm(f => f && f.map((t, j) => j === i ? { ...t, ...patch } : t));
  }
  function move(i: number, dir: -1 | 1) {
    setForm(f => {
      if (!f) return f;
      const target = i + dir;
      if (target < 0 || target >= f.length) return f;
      const next = [...f];
      [next[i], next[target]] = [next[target], next[i]];
      return next;
    });
  }
  function handleSave() {
    if (!form) return;
    const cleaned = form.filter(t => t.label.trim()).map(t => ({
      ...t, label: t.label.trim(), options: t.options.map(o => o.trim()).filter(Boolean),
    }));
    for (const t of cleaned) {
      if (needsOptions(t.field_type) && t.options.length < 2) {
        toast(`"${t.label}" needs at least 2 options.`, "error");
        return;
      }
    }
    mutation.mutate({ templates: cleaned });
  }

  if (isLoading || !form) return <div style={{ color: "#64748b", fontSize: 13 }}>Loading…</div>;

  return (
    <>
      <LoadingOverlay active={mutation.isPending} />
      <div style={card}>
        <p style={{ fontSize: 13, color: "#64748b", marginBottom: 16, lineHeight: 1.6 }}>
          Reusable Application Screening Question templates — quick-add any of these from a job's own
          Screening Questions section instead of retyping common ones. Editing or removing a template
          here never changes a question already added to a job from it.
        </p>

        {form.length === 0 && <div style={{ fontSize: 13, color: "#94a3b8", marginBottom: 14 }}>No templates yet.</div>}

        {form.map((t, i) => (
          <div key={t.id} style={{ display: "flex", gap: 8, marginBottom: 10, alignItems: "flex-start", flexWrap: "wrap", border: "1px solid #e2e8f0", borderRadius: 8, padding: 12 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 2, paddingTop: 6 }}>
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} style={reorderBtnStyle}>▲</button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === form.length - 1} style={reorderBtnStyle}>▼</button>
            </div>
            <input
              value={t.label}
              placeholder="Question label (e.g. Expected Salary)"
              onChange={e => updateRow(i, { label: e.target.value })}
              style={{ ...inputStyle, width: 220 }}
            />
            <select
              value={t.field_type}
              onChange={e => {
                const field_type = e.target.value as QuestionFieldType;
                updateRow(i, { field_type, options: needsOptions(field_type) ? t.options : [] });
              }}
              style={{ ...inputStyle, width: 140 }}
            >
              {Object.entries(FIELD_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            {needsOptions(t.field_type) && (
              <textarea
                value={t.options.join("\n")}
                placeholder={"Options, one per line\n(min 2)"}
                onChange={e => updateRow(i, { options: e.target.value.split("\n") })}
                style={{ ...inputStyle, flex: 1, minWidth: 180, minHeight: 40, resize: "vertical", fontFamily: "inherit" }}
              />
            )}
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#374151", paddingTop: 8, whiteSpace: "nowrap" }}>
              <input type="checkbox" checked={t.required} onChange={e => updateRow(i, { required: e.target.checked })} />
              Required by default
            </label>
            <button type="button" onClick={() => setForm(form.filter((_, j) => j !== i))} style={{ ...outlineBtn, padding: "8px 10px" }} title="Remove">×</button>
          </div>
        ))}

        <button type="button" onClick={() => setForm([...form, BLANK_TEMPLATE()])} style={{ ...outlineBtn, marginBottom: 18 }}>
          + Add Template
        </button>

        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <button onClick={handleSave} disabled={mutation.isPending} style={saveBtn}>
            {mutation.isPending ? "Saving…" : "Save Changes"}
          </button>
          {saved && <span style={{ fontSize: 13, color: "#16a34a" }}>Saved!</span>}
          <button onClick={handleReset} disabled={mutation.isPending} style={outlineBtn}>Reset to Defaults</button>
        </div>
      </div>
    </>
  );
}

// ── Notifications (per-recruiter new-applicant email preference) ────────────

const NOTIFY_OPTIONS = [
  { value: "instant", label: "Instant", desc: "One email per new application, as it arrives." },
  { value: "digest", label: "Daily digest", desc: "One summary email per day listing all new applicants. Days with no applicants send nothing." },
  { value: "off", label: "Off", desc: "No applicant emails. In-app notifications still appear." },
];

function NotificationsTab() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { data, isLoading } = useQuery({ queryKey: ["applicant-notify-pref"], queryFn: getApplicantNotifyPref });
  const [saved, setSaved] = useState(false);

  const mutation = useMutation({
    mutationFn: saveApplicantNotifyPref,
    onSuccess: () => {
      setSaved(true);
      qc.invalidateQueries({ queryKey: ["applicant-notify-pref"] });
      setTimeout(() => setSaved(false), 3000);
    },
    onError: () => toast("Failed to save notification preference", "error"),
  });

  if (isLoading || !data) return <div style={{ color: "#64748b", fontSize: 13 }}>Loading…</div>;

  return (
    <>
      <LoadingOverlay active={mutation.isPending} />
      <div style={card}>
        <p style={{ fontSize: 13, color: "#64748b", marginBottom: 16, lineHeight: 1.6 }}>
          How <strong>you</strong> get emailed about new applicants. This is your personal setting — each
          recruiter on the team picks their own.
        </p>

        {!data.email_available && (
          <div style={{ background: "#fef9c3", border: "1px solid #fde047", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#854d0e", marginBottom: 16 }}>
            Email is not configured yet, so no emails will be sent. Your preference is still saved
            and takes effect once an admin configures SMTP in Settings.
          </div>
        )}

        {NOTIFY_OPTIONS.map(opt => (
          <label key={opt.value} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 14px", border: `1px solid ${data.preference === opt.value ? NAVY : "#e2e8f0"}`, borderRadius: 8, marginBottom: 10, cursor: "pointer", background: data.preference === opt.value ? "#f0f5ff" : "white" }}>
            <input
              type="radio"
              name="notify-pref"
              checked={data.preference === opt.value}
              onChange={() => mutation.mutate(opt.value)}
              style={{ marginTop: 3 }}
            />
            <span>
              <span style={{ display: "block", fontSize: 14, fontWeight: 600, color: "#0f172a" }}>{opt.label}</span>
              <span style={{ display: "block", fontSize: 12, color: "#64748b", marginTop: 2 }}>{opt.desc}</span>
            </span>
          </label>
        ))}
        {saved && <span style={{ fontSize: 13, color: "#16a34a" }}>Saved!</span>}
      </div>
    </>
  );
}
